import {
    Input,
    Button,
    Typography
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, DocumentPlusIcon, EyeIcon, EyeSlashIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { deleteOtherDocument, addCustomer, resetStatus, getBusinessType } from "../../store/slices/customerSlice";
import ShowLoader from "@/components/Loader";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import { fetchCountryMobileCode } from "../../store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";
import { useTranslation } from "react-i18next";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\|,.<>\/?]$).*$/;

const fileValidation = (field, isEdit, isImageRemoved, t) =>
    yup
        .mixed()
        .test("fileType", t('validations.file_type_image'), (value) => {
            if (!value || !(value instanceof File)) return true;
            return value.type.startsWith("image/");
        })
        .nullable()
        .when([], {
            is: () => !isEdit || isImageRemoved[field],
            then: (schema) =>
                schema.required(
                    field === "residence_image"
                        ? t('validations.residence_image_required')
                        : t('validations.image_required', { field: field.replaceAll("_", " ").replace(/^./, (str) => str.toUpperCase()) })
                ),
            otherwise: (schema) => schema.notRequired(),
        });


const schema = (isEdit = false, type, isImageRemoved = {}, t) =>
    yup.object({
        first_name: yup
            .string()
            .trim()
            .required(t('validations.first_name_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.min_2'))
            .max(30, t('validations.max_30')),

        last_name: yup
            .string()
            .trim()
            .required(t('validations.last_name_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.min_2'))
            .max(30, t('validations.max_30')),

        company_name: yup
            .string()
            .trim()
            .required(t('validations.company_name_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.min_2'))
            .max(50, t('validations.max_50')),

        business_type: yup
            .string()
            .required(t('validations.business_type_required'))
            .min(1, t('validations.min_2'))
            .max(50, t('validations.max_50')),

        email: yup
            .string()
            .trim()
            .required(t('validations.email_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .email(t('validations.invalid_email'))
            .max(50, t('validations.max_50')),

        mobile_number: yup
            .string()
            .trim()
            .required(t('validations.phone_number_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(/^\d+$/, t('validations.only_digits'))
            .min(8, t('validations.min_8'))
            .max(15, t('validations.max_15'))
            .test("not-all-zeros", t('validations.not_all_zeros'), (value) => {
                if (!value) return false;
                return !/^0+$/.test(value);
            }),

        country_code: yup
            .string()
            .trim()
            .required(t('validations.phone_code_required'))
            .max(5, t('validations.country_code_max')),

        alternate_number: yup
            .string()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .trim()
            .test("not-blank", t('validations.not_blank'), (value) => !value || notBlankRegex.test(value))
            .test("is-valid", t('validations.alternate_number_is_valid'), (value) => !value || /^[0-9]{8,15}$/.test(value))
            .test("not-all-zeros", t('validations.not_all_zeros'), (value) => !value || !/^0+$/.test(value)),

        alternate_country_code: yup
            .string()
            .nullable()
            .trim()
            .max(5, t('validations.alternate_mobile_code_max'))
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .when("alternate_number", {
                is: (val) => val && val.trim() !== "",
                then: (schema) => schema.required(t('validations.alternate_mobile_code_required')),
                otherwise: (schema) => schema.nullable(),
            }),

        password: yup
            .string()
            .when([], {
                is: () => !isEdit,
                then: (schema) =>
                    schema
                        .required(t('validations.password_required'))
                        .matches(/\S/, t('validations.password_no_spaces'))
                        .min(8, t('validations.password_min'))
                        .max(15, t('validations.password_max'))
                        .test(
                            "is-strong",
                            t('validations.password_weak'),
                            (value) =>
                                value &&
                                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(value)
                        ),
                otherwise: (schema) => schema.notRequired(),
            }),

        password_confirmation: yup
            .string()
            .when("password", {
                is: (val) => val && val.length > 0,
                then: (schema) =>
                    schema
                        .required(t('validations.confirm_password_required'))
                        .oneOf([yup.ref("password"), null], t('validations.password_match')) // run this second
                        .min(8, t('validations.password_min'))
                        .max(15, t('validations.password_max')),
                otherwise: (schema) => schema.notRequired(),
            }),

        permanent_address: yup
            .string()
            .when([], {
                is: () => type === "Vendor" || type === "Customer",
                then: (schema) =>
                    schema
                        .required(t('validations.national_address_required'))
                        .min(2, t('validations.national_address_min'))
                        .max(100, t('validations.national_address_max')),
                otherwise: (schema) => schema.notRequired(),
            })
            .trim()
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct')),

        address_proof_image: fileValidation("address_proof_image", isEdit, isImageRemoved, t),
        commercial_reg_image: fileValidation("commercial_reg_image", isEdit, isImageRemoved, t),
        vat_certificate_image: fileValidation("vat_certificate_image", isEdit, isImageRemoved, t),
        passbook_image: fileValidation("passbook_image", isEdit, isImageRemoved, t),
        residence_image: fileValidation("residence_image", isEdit, isImageRemoved, t),

        commercial_reg_number: yup
            .string()
            .trim()
            .required(t('validations.commercial_reg_number_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.commercial_reg_number_min'))
            .max(30, t('validations.commercial_reg_number_max')),

        vat_certificate_number: yup
            .string()
            .trim()
            .required(t('validations.vat_certificate_number_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.vat_certificate_number_min'))
            .max(30, t('validations.vat_certificate_number_max')),

        account_number: yup
            .string()
            .trim()
            .required(t('validations.account_number_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(8, t('validations.account_number_min'))
            .max(15, t('validations.account_number_max')),

        bank_swift_code: yup
            .string()
            .required(t('validations.bank_iban_required'))
            .length(24, t('validations.bank_iban_length'))
            .matches(/^[A-Z0-9]{24}$/, t('validations.bank_iban_format')),

        account_holder_first_name: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .trim()
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.min_2'))
            .max(30, t('validations.max_30')),

        other_documents: yup
            .mixed()
            .nullable()
            .test("fileType", t('validations.other_documents_file_type'), (value) => {
                if (!value) return true;
                const files = Array.isArray(value) ? value : [value];
                return files.every((file) => {
                    if (typeof file === "string") return true;

                    if (file?.file && typeof file.file === "string") return true;

                    if (file instanceof File) {
                        return file.type.startsWith("image/") || file.type === "application/pdf";
                    }

                    return false;
                });
            })
            .test("fileCount", t('validations.other_documents_file_count'), (value) => {
                if (!value) return true;
                const files = Array.isArray(value) ? value : [value];
                return files.length <= 5;
            }),

        residence_id: yup
            .string()
            .trim()
            .required(t('validations.residence_id_required'))
            .matches(notBlankRegex, t('validations.not_blank'))
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .min(2, t('validations.residence_id_min_max'))
            .max(30, t('validations.residence_id_min_max')),

        other_documents_detail: yup
            .string()
            .trim()
            .nullable()
            .test(
                "not-only-spaces",
                t('validations.other_documents_detail_no_spaces'),
                (value) => {
                    if (value === null || value === undefined) return true;
                    if (value === "") return true;
                    return value.trim().length > 0;
                }
            )
            .matches(noHTMLRegex, t('validations.no_html'))
            .matches(noCodeRegex, t('validations.no_code'))
            .matches(noConsecutiveSpecialChars, t('validations.no_consecutive_special'))
            .matches(noLeadingTrailingPunct, t('validations.no_leading_trailing_punct'))
            .max(100, t('validations.other_documents_detail_max')),
    });

const isImageRemoved = {
    address_proof_image: false,
    commercial_reg_image: false,
    vat_certificate_image: false,
    passbook_image: false,
    residence_image: false,
};


export function AddCustomerMember({ type = 'customer', isEdit = false, isView = false }) {
    const { t } = useTranslation("customer");
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const [isShowLoader, setIsShowLoader] = useState(true);

    const initialForm = {
        id: null,
        first_name: '',
        last_name: '',
        company_name: '',
        business_type: '',
        email: '',
        mobile_number: null,
        country_code: null,
        alternate_number: null,
        alternate_country_code: null,
        password: '',
        password_confirmation: '',
        permanent_address: null,
        address_proof_image: '',
        commercial_reg_number: '',
        commercial_reg_image: '',
        vat_certificate_number: '',
        vat_certificate_image: '',
        account_number: '',
        passbook_image: null,
        bank_swift_code: 'SA',
        account_holder_first_name: null,
        // account_holder_last_name: null,
        other_documents: [],
        role: type,
        residence_id: '',
        residence_image: null,
        other_documents_detail: null
    };

    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema(isEdit, type, isImageRemoved, t)),
        defaultValues: initialForm
    });

    useEffect(() => {
        dispatch(getBusinessType());
        dispatch(fetchCountryMobileCode());
        if (!isEdit) {
            setIsShowLoader(false);
        }
    }, []);

    const selectedCustomer = useSelector((s) => s.customer.selected);
    const { loading, business_types } = useSelector((state) => state.customer);
    const { mobile_country_code } = useSelector((state) => state.internalUser);

    const other_documents = watch('other_documents') || [];
    const passbook_image = watch('passbook_image');
    const residence_image = watch('residence_image');
    const address_proof_image = watch('address_proof_image');
    const vat_certificate_image = watch('vat_certificate_image');
    const commercial_reg_image = watch('commercial_reg_image');

    useEffect(() => {
        if (isEdit) {
            if (selectedCustomer && String(selectedCustomer.id) === String(id)) {
                reset({ ...selectedCustomer });
            }
            setIsShowLoader(false);
        } else {
            reset(initialForm);
        }
    }, [isEdit, id, selectedCustomer, dispatch]);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const togglePassword = () => {
        setShowPassword(!showPassword);
    }
    const toggleConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    }

    const onSubmit = async (data) => {

        try {
            const result = await dispatch(addCustomer(data)).unwrap();
            if (result.status) {
                Toaster.success(result.message || t('user_added_successfully'));
                dispatch(resetStatus());
                reset();
                navigate(`/dashboard/customer-members/${selectedCustomer.parent_id}`);
            } else {
                Toaster.error(result.message || t('something_went_wrong'));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t('failed_to_add_user'));
            dispatch(resetStatus());
        }
    };

    const handleFileChange = (e, field) => {
        const files = Array.from(e.target.files);

        if (field === "other_documents") {
            const existingDocs = watch("other_documents") || [];

            setValue("other_documents", [...existingDocs, ...files], { shouldValidate: true });
        } else {
            setValue(field, files[0], { shouldValidate: true });
        }
        if (isEdit) {
            isImageRemoved[field] = false;
        }
    };

    const removeFile = (field) => {
        setValue(field, null, { shouldValidate: false });
        if (isEdit) {
            isImageRemoved[field] = true;
        }
    };

    const removeDocument = (index, documentId = null) => {
        const updated = [...other_documents];
        const removedDoc = updated.splice(index, 1);
        setValue("other_documents", updated);

        if (documentId) {
            Swal.fire({
                title: t('are_you_sure'),
                text: t('you_wont_be_able_to_revert_this'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t('yes_delete_it')
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(deleteOtherDocument(documentId)).unwrap().then(() => {
                        Toaster.success(t('other_document_deleted_successfully'));
                    }).catch((error) => {
                        Toaster.error(t('failed_to_delete_document'));
                        setValue("other_documents", [...updated, removedDoc[0]]);
                    });
                } else {
                    setValue("other_documents", [...updated, removedDoc[0]]);
                }
            });
        }
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(loading || isShowLoader) && <ShowLoader />}
            <ScrollToTop />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
                        <div className="flex items-center gap-2 cursor-pointer">
                            <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                                {isView ? t('view_team_member', { type: type }) : isEdit ? t('edit_team_member', { type: type }) : t('add_new_team_member', { type: type })}
                            </Typography>
                        </div>
                        {!isView && <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => navigate(`/dashboard/customer-members/${selectedCustomer.parent_id}`)}
                                className="rounded-lg"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                                {loading ? t('saving') : isEdit ? t('update') : t('save')}
                            </Button>
                        </div>}
                    </div>
                    <div className="flex items-center gap-1">
                        <Link to={`/dashboard/customer-members/${selectedCustomer.parent_id}`} className="flex items-center text-blue-600 hover:text-blue-800">
                            <ArrowLeftIcon className="h-4 w-4 mr-1" />
                            {t('back')}
                        </Link>
                    </div>
                </div>
                <fieldset disabled={isView}>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('basic_details')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('first_name')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_first_name')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("first_name")}
                                    error={Boolean(errors.first_name)}
                                />
                                {errors.first_name && (
                                    <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('last_name')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_last_name')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("last_name")}
                                    error={Boolean(errors.last_name)}
                                />
                                {errors.last_name && (
                                    <p className="text-red-500 text-sm">{errors.last_name?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('registered_company_name')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_company_name')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("company_name")}
                                    error={Boolean(errors.company_name)}
                                />
                                {errors.company_name && (
                                    <p className="text-red-500 text-sm">{errors.company_name?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('business_type')} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('select_business_type')}
                                    onChange={(business_type) => {
                                        setValue('business_type', business_type?.value, { shouldValidate: true });
                                    }}
                                    error={Boolean(errors.business_type)}
                                    options={business_types}
                                    value={business_types?.find(opt => opt.value == watch('business_type')) || null}
                                    isClearable
                                    isDisabled={isView}
                                />
                                {errors.business_type && (
                                    <p className="text-red-500 text-sm">{errors.business_type?.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('phone_number')} <span className="text-red-500">*</span>{t('with_whatsapp_access')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            size="lg"
                                            className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            placeholder={t('select')}
                                            onChange={(code) => {
                                                setValue('country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('enter_phone_number')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("mobile_number")}
                                            error={Boolean(errors.mobile_number)}
                                        />
                                    </div>
                                </div>
                                {(errors.mobile_number || errors.country_code) && (
                                    <p className="text-red-500 text-sm">{errors.mobile_number?.message ? errors.mobile_number?.message : errors.country_code.message}</p>
                                )}
                            </div>
                            <div className="xl:col-span-1 flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('alternate_number')}
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                    <div className="md:col-span-3">
                                        <Select
                                            type="text"
                                            placeholder={t('select')}
                                            onChange={(code) => {
                                                setValue('alternate_country_code', code?.value, { shouldValidate: true });
                                            }}
                                            value={mobile_country_code?.find(opt => opt.value === watch('alternate_country_code')) || null}
                                            options={mobile_country_code}
                                            error={Boolean(errors.alternate_country_code)}
                                            isDisabled={isView}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    minHeight: '44px',
                                                    height: '44px',
                                                }),
                                            }}
                                            components={{
                                                Option: CountryOption,
                                                SingleValue: CountrySingleValue
                                            }}
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <Input
                                            type="number"
                                            size="lg"
                                            min='0'
                                            placeholder={t('enter_alternate_number')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("alternate_number")}
                                            error={Boolean(errors.alternate_number)}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (!value || value.trim() === "") {
                                                    setValue("alternate_number", '', { shouldValidate: true });
                                                    setValue("alternate_country_code", null, { shouldValidate: true });
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                {(errors.alternate_number || errors.alternate_country_code) && (
                                    <p className="text-red-500 text-sm">{errors.alternate_number?.message ? errors.alternate_number?.message : errors.alternate_country_code.message}</p>
                                )}
                            </div>
                            {!isEdit && <>
                                <div className="flex flex-col gap-1">
                                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                        {t('set_password')} <span className="text-red-500">*</span>
                                    </Typography>
                                    <div className="password-wrapper">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            size="lg"
                                            placeholder="********"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("password")}
                                            error={Boolean(errors.password)}
                                        />
                                        {showPassword ? (
                                            <EyeIcon className="eye-icon" onClick={togglePassword} />
                                        ) : (
                                            <EyeSlashIcon className="eye-icon" onClick={togglePassword}></EyeSlashIcon>
                                        )}
                                    </div>
                                    {errors.password && (
                                        <p className="text-red-500 text-sm">{errors.password?.message}</p>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                        {t('confirm_password')} <span className="text-red-500">*</span>
                                    </Typography>
                                    <div className="password-wrapper">
                                        <Input
                                            type={showConfirmPassword ? "text" : "password"}
                                            size="lg"
                                            placeholder="********"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("password_confirmation")}
                                            error={Boolean(errors.password_confirmation)}
                                        />
                                        {showConfirmPassword ? (
                                            <EyeIcon className="eye-icon" onClick={toggleConfirmPassword} />
                                        ) : (
                                            <EyeSlashIcon className="eye-icon" onClick={toggleConfirmPassword}></EyeSlashIcon>
                                        )}
                                    </div>
                                    {errors.password_confirmation && (
                                        <p className="text-red-500 text-sm">{errors.password_confirmation?.message}</p>
                                    )}
                                </div>
                            </>}
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('email_address')} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="email"
                                    size="lg"
                                    placeholder={t('enter_email_address')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("email")}
                                    error={Boolean(errors.email)}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm">{errors.email?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('residence_id_or_iqama_id')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        size="lg"
                                        placeholder={t('enter_residence_id_or_iqama_id')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("residence_id")}
                                        error={Boolean(errors.residence_id)}
                                    />
                                    <input
                                        id="residenceNumberFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        {...register("residence_image")}
                                        onChange={(e) => handleFileChange(e, 'residence_image')}
                                    />
                                    <label
                                        htmlFor="residenceNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                {(errors.residence_id || errors.residence_image) && (
                                    <p className="text-red-500 text-sm">{errors.residence_id?.message ? errors.residence_id?.message : errors.residence_image?.message}</p>
                                )}
                            </div>
                            {residence_image && <>
                                <div className="flex flex-col gap-1">
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                        <FilePreview
                                            file={residence_image}
                                            onRemove={() => removeFile("residence_image")}
                                            isView={isView}
                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                        />
                                    </div>
                                </div>
                            </>}
                        </div>
                    </div>
                    <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                        <div className="mb-5 pb-2 border-b border-blue-gray-200">
                            <Typography variant="h4" color="blue-gray">
                                {t('kyc_details')}
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            {type === 'Vendor' && <>
                                <div className="md:col-span-2">
                                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                        {t('national_address')} <span className="text-red-500">*</span>
                                    </Typography>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder={t('national_address_placeholder')}
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("permanent_address")}
                                            error={Boolean(errors.permanent_address)}
                                        />
                                        <input
                                            id="addressFile"
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            {...register("address_proof_image")}
                                            onChange={(e) => handleFileChange(e, 'address_proof_image')}
                                        />
                                        <label
                                            htmlFor="addressFile"
                                            className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                        >
                                            <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                        </label>
                                    </div>
                                    {(errors.permanent_address || errors.address_proof_image) && (
                                        <p className="text-red-500 text-sm">{errors.permanent_address?.message ? errors.permanent_address?.message : errors.address_proof_image?.message}</p>
                                    )}
                                </div>
                                {address_proof_image &&
                                    <div className="md:col-span-2">
                                        <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                            <FilePreview
                                                file={address_proof_image}
                                                onRemove={() => removeFile("address_proof_image")}
                                                isView={isView}
                                                containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                            />
                                        </div>
                                    </div>}
                            </>}
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('commercial_registration_certificate_number')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="text"
                                        size="lg"
                                        placeholder={t('enter_certificate_number')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("commercial_reg_number")}
                                        error={Boolean(errors.commercial_reg_number)}
                                    />
                                    <input
                                        id="commercialNumberFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleFileChange(e, 'commercial_reg_image')}
                                    />
                                    <label
                                        htmlFor="commercialNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                {(errors.commercial_reg_number || errors.commercial_reg_image) && (
                                    <p className="text-red-500 text-sm">{errors.commercial_reg_number?.message ? errors.commercial_reg_number?.message : errors.commercial_reg_image?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('vat_certificate_number_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="text"
                                        size="lg"
                                        placeholder={t('enter_vat_number')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("vat_certificate_number")}
                                        error={Boolean(errors.vat_certificate_number)}
                                    />
                                    <input
                                        id="vatNumberFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleFileChange(e, 'vat_certificate_image')}
                                    />
                                    <label
                                        htmlFor="vatNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                {(errors.vat_certificate_number || errors.vat_certificate_image) && (
                                    <p className="text-red-500 text-sm">{errors.vat_certificate_number?.message ? errors.vat_certificate_number?.message : errors.vat_certificate_image?.message}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                {commercial_reg_image && (
                                    <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                        <FilePreview
                                            file={commercial_reg_image}
                                            onRemove={() => removeFile("commercial_reg_image")}
                                            isView={isView}
                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                {vat_certificate_image && (
                                    <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                        <FilePreview
                                            file={vat_certificate_image}
                                            onRemove={() => removeFile("vat_certificate_image")}
                                            isView={isView}
                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('account_number_label')} <span className="text-red-500">*</span>
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        size="lg"
                                        placeholder={t('enter_account_number')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register("account_number")}
                                        error={Boolean(errors.account_number)}
                                    />
                                    <input
                                        id="accountNumberFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleFileChange(e, 'passbook_image')}
                                    />
                                    <label
                                        htmlFor="accountNumberFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t('choose_file')}
                                    </label>
                                </div>
                                {(errors.account_number || errors.passbook_image) && (
                                    <p className="text-red-500 text-sm">{errors.account_number?.message ? errors.account_number?.message : errors.passbook_image?.message}</p>
                                )}

                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('bank_iban_label')} <span className="text-red-500">*</span>
                                </Typography>
.
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_bank_iban')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("bank_swift_code", {
                                        onChange: (e) => {
                                            e.target.value = e.target.value.toUpperCase();
                                        },
                                    })}
                                    error={Boolean(errors.bank_swift_code)}
                                />
                                {errors.bank_swift_code && (
                                    <p className="text-red-500 text-sm">{errors.bank_swift_code?.message}</p>
                                )}
                            </div>
                            {passbook_image && <>
                                <div className="md:col-span-2">
                                    <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                        <FilePreview
                                            file={passbook_image}
                                            onRemove={() => removeFile("passbook_image")}
                                            isView={isView}
                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                        />
                                    </div>
                                </div>
                            </>}
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('account_holder_first_name_label')}
                                </Typography>
                                <Input
                                    type="text"
                                    size="lg"
                                    placeholder={t('enter_full_name')}
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    {...register("account_holder_first_name")}
                                    error={Boolean(errors.account_holder_first_name)}
                                />
                                {errors.account_holder_first_name && (
                                    <p className="text-red-500 text-sm">{errors.account_holder_first_name?.message}</p>
                                )}
                            </div>

                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('other_documents_label')}
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="text"
                                        size="lg"
                                        placeholder={t('other_documents_placeholder')}
                                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        {...register('other_documents_detail')}
                                    />
                                    <input
                                        id="otherDocumentFile"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={(e) => handleFileChange(e, "other_documents")}
                                    />
                                    <label
                                        htmlFor="otherDocumentFile"
                                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                    >
                                        <DocumentPlusIcon className="h-4 w-4 text-green-800" />
                                        {t('choose_file')}
                                    </label>
                                </div>
                                {errors?.other_documents && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.other_documents.message || "Invalid file type"}
                                    </p>
                                )}
                            </div>
                            {other_documents.length > 0 && (
                                <div className="md:col-span-2">
                                    <div className="mt-3 flex flex-wrap gap-6">
                                        {other_documents?.map((doc, index) => {
                                            const isExisting = !!doc?.id;
                                            const file = isExisting ? doc.file : doc;
                                            return (
                                                <FilePreview
                                                    key={index}
                                                    file={file}
                                                    onRemove={() => removeDocument(index, isExisting ? doc.id : "")}
                                                    isView={isView}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                    className="object-cover w-full h-full"
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </fieldset>
            </form>
        </div>
    );
}

export default AddCustomerMember;
